import Contenteditable from '@creator/ui/components/Contenteditable/Contenteditable';
import Linkify from '@creator/ui/components/Linkify/Linkify';
import ReadMore from '@creator/ui/components/ReadMore/ReadMore';
import Text from '@creator/ui/components/Text/Text';
import { cn } from '@creator/ui/utils/ui';
import { canViewPendingComment } from '@src/model/upvote/permission';
import { useComment } from '@src/store/comments/hooks/useComment';
import { CommentStatus } from '@src/store/contentEntities/model';
import { Fragment, memo } from 'react';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { CommentModScore } from './CommentModScore';
interface CommentContentProps {
    commentId: string;
    className?: string;
    tokenName: string;
    isEditMode: boolean;
    draft: string;
    hideStatus?: boolean;
    onChange: (content: string) => void;
    onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
    onBlur?: () => void;
}

const CommentContent: FC<CommentContentProps> = props => {
    const { className = '', commentId, isEditMode, draft, onChange, onKeyDown, onBlur, tokenName } = props;

    const comment = useComment(commentId);
    const commentContent = comment?.content || '';
    const { t } = useTranslation();

    const getBaseClassName = () => {
        const base = 'leading-tight whitespace-pre-line break-normal transition';
        if (isEditMode) return `${base} focus:ring-1 focus:ring-primary-500`;
        return `${base} space-y-1`;
    };

    function renderStatus() {
        const { hideStatus = false } = props;
        if (!comment || hideStatus) return null;
        if (comment.status === CommentStatus.PUBLISHED) return null;
        return <Text className="block text-gray-500">{t(`commentStatusIndicator.status${comment.status}`)}</Text>;
    }

    function renderContenteditable() {
        return <Contenteditable onKeyDown={onKeyDown} onBlur={onBlur} onInput={onChange} className={cn(getBaseClassName(), className)} value={draft} />;
    }

    function getContent() {
        if (!comment) return '';
        const { status } = comment;
        if (status === CommentStatus.PUBLISHED) return commentContent;
        if (status === CommentStatus.PENDING && canViewPendingComment(commentId)) return commentContent;
    }

    function renderReadMore() {
        const content = getContent();
        if (!content) return null;
        return (
            <ReadMore lineClamp={6} className={cn(getBaseClassName(), className)}>
                <Linkify>{getContent()}</Linkify>
            </ReadMore>
        );
    }

    function renderContent() {
        if (!comment) return;
        if (isEditMode) return renderContenteditable();
        return (
            <Fragment>
                <CommentModScore commentId={commentId} tokenName={tokenName} />
                {renderStatus()}
                {renderReadMore()}
            </Fragment>
        );
    }

    return renderContent();
};

export default memo(CommentContent);
